import { CloudUploadOutlined } from '@mui/icons-material';
import { CircularProgress, Paper, styled, Typography } from '@mui/material';
import React, { useCallback, useRef, useState } from 'react';

type FileDropzoneProps = {
  label?: string;
  hint?: string;
  accept?: string;
  onFilesAdded?: (files: File[]) => Promise<void> | void;
};

const UploadArea = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'dragActive',
})<{ dragActive?: boolean }>(({ theme, dragActive }) => ({
  borderStyle: 'dashed',
  borderColor: dragActive ? theme.palette.primary.main : '#ccc',
  borderWidth: 2,
  borderRadius: 8,
  padding: theme.spacing(2),
  textAlign: 'center',
  cursor: 'pointer',
  backgroundColor: dragActive ? '#f0f8ff' : 'transparent',
  transition: 'all 0.2s ease-in-out',
}));

const UploadIcon = styled(CloudUploadOutlined)(() => ({
  fontSize: 36,
  color: '#666',
}));

const FileDropzone = ({
  label = 'Drag & drop files here',
  hint = 'or click to upload',
  accept,
  onFilesAdded,
}: FileDropzoneProps): JSX.Element => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);

  const processFiles = useCallback(
    async (files: File[]) => {
      if (!onFilesAdded || files.length === 0) return;
      setLoading(true);
      try {
        await onFilesAdded(files);
      } finally {
        setLoading(false);
      }
    },
    [onFilesAdded]
  );

  const handleDrag = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    if (e.type === 'dragleave') setDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      processFiles(Array.from(e.dataTransfer.files));
    },
    [processFiles]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files ? Array.from(e.target.files) : [];
      processFiles(files);
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [processFiles]
  );

  const openFileDialog = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!loading) fileInputRef.current?.click();
    },
    [loading]
  );

  return (
    <UploadArea
      dragActive={dragActive}
      elevation={0}
      variant="outlined"
      onClick={openFileDialog}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      sx={{ pointerEvents: loading ? 'none' : 'auto' }}
    >
      {loading ? (
        <CircularProgress size={40} />
      ) : (
        <>
          <UploadIcon />
          <Typography>{dragActive ? 'Drop files here' : label}</Typography>
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        </>
      )}

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept={accept}
        onChange={handleFileChange}
      />
    </UploadArea>
  );
};

export default FileDropzone;
